import { ReactNode } from "react";
import { ArrowIcon } from "components/common/Icons/ArrowIcon";
import { NavLinkParent } from "../../NavLinkParent/NavLinkParent";
import { useDesktopNavLink } from "./useDesktopNavLink";
import translatedRoutedSlugs from "translatedRouteSlugs";
import { useRouter } from "next/router";
import { isNavColumns, type NavChild, type NavColumn } from "components/modules/Navigation/navLinks";

export interface DesktopNavLinkProps {
    title: string | ReactNode;
    link: string;
    isEditor: boolean;
    outlink?: boolean;
    isChild?: boolean;
    subItems?: NavChild[] | NavColumn[];
    urlMatcher?: (url: string, asPath: string, locale: string) => boolean;
}

export const DesktopNavLink = ({
    link,
    title,
    isEditor,
    outlink,
    isChild = false,
    subItems,
    urlMatcher,
}: DesktopNavLinkProps) => {
    const { hasChildren, isFocused, setIsFocused, isActive, ref } = useDesktopNavLink(
        subItems,
        isChild,
        link,
        urlMatcher,
    );
    const { locale } = useRouter();
    // External links (e.g. the ClipSnap AI tools) must keep their absolute URL.
    if (!outlink) {
        link =
            link === "/"
                ? "/"
                : "/" + ((translatedRoutedSlugs as any)[link]?.[locale ?? "en"] ?? link.replaceAll("/", ""));
    }

    return (
        <li
            onClick={() => {
                hasChildren && setIsFocused(!isFocused);
            }}
            onMouseEnter={() => {
                hasChildren && setIsFocused(true);
            }}
            onMouseLeave={() => {
                hasChildren && setIsFocused(false);
            }}
            className="relative w-full"
            ref={ref}
            style={{
                fontSize: "15px",
            }}
        >
            <NavLinkParent
                link={link}
                isParent={!!hasChildren}
                isChild={!!isChild}
                twClasses={"light:hover:bg-slate-200 dark:hover:bg-darkSurface-200"}
                outlink={outlink}
                isEditor={isEditor}
            >
                <div className={`whitespace-nowrap ${isActive && `font-semibold`}`}>{title}</div>
                {subItems &&
                    subItems.length &&
                    (isFocused ? (
                        <div className="ml-2 rotate-180">
                            <ArrowIcon />
                        </div>
                    ) : (
                        <div className="ml-2">
                            <ArrowIcon />
                        </div>
                    ))}
            </NavLinkParent>

            {hasChildren && isFocused && subItems && (
                <ul className="shadow-2xl absolute top-full right-auto left-0 bottom-auto flex flex-col justify-start gap-2 bg-white dark:bg-darkSurface-200 rounded-md overflow-hidden z-30">
                    {isNavColumns(subItems) ? (
                        <div className="flex items-start">
                            {subItems.map((column, columnIndex) => (
                                <ul key={columnIndex} className="flex flex-col justify-start gap-2">
                                    {column.header && (
                                        <li className="whitespace-nowrap px-3 pt-4 pb-1 font-bold text-black dark:text-white lg:pl-6 lg:pr-24">
                                            {column.header}
                                        </li>
                                    )}
                                    {column.items.map((item, index) => (
                                        <DesktopNavLink
                                            key={index}
                                            link={item.link}
                                            title={item.title}
                                            isEditor={isEditor}
                                            outlink={item.outlink}
                                            isChild={true}
                                            urlMatcher={item.urlMatcher}
                                        />
                                    ))}
                                </ul>
                            ))}
                        </div>
                    ) : (
                        (subItems as NavChild[]).map((item, index) => {
                            return (
                                <DesktopNavLink
                                    key={index}
                                    link={item.link}
                                    title={item.title}
                                    isEditor={isEditor}
                                    outlink={item.outlink}
                                    isChild={true}
                                    urlMatcher={item.urlMatcher}
                                />
                            );
                        })
                    )}
                </ul>
            )}
        </li>
    );
};
